
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PromoGroup } from '@/api/entities';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription, // Added DialogDescription import
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent } from "@/components/ui/card";

const ColorPickerInput = ({ label, value, onChange }) => (
    <div>
        <Label className="text-sm font-medium text-slate-700">{label}</Label>
        <div className="flex items-center gap-2 p-2 border border-slate-300 rounded-md mt-1">
            <input
                type="color"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-8 h-8 p-0 border-none cursor-pointer bg-transparent"
            />
            <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="border-none focus-visible:ring-0"
            />
        </div>
    </div>
);

export default function PromoGroupForm({ isOpen, onClose, group, onSave }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    priority: 1,
    font_size: 24,
    font_weight: 'bold',
    text_color: '#1e293b',
    card_variant: 'small',
    is_active: true
  });
  const [isLoading, setIsLoading] = useState(false);

  // Determine if we are editing an existing group or creating a new one
  const isEditing = !!group;

  useEffect(() => {
    if (group) {
      setFormData({
        name: group.name || '',
        priority: group.priority || 1,
        font_size: group.font_size || 24,
        font_weight: group.font_weight || 'bold',
        text_color: group.text_color || '#1e293b',
        card_variant: group.card_variant || 'small',
        is_active: group.is_active !== undefined ? group.is_active : true
      });
    } else {
      // Reset form data when creating a new group
      setFormData({
        name: '',
        priority: 1,
        font_size: 24,
        font_weight: 'bold',
        text_color: '#1e293b',
        card_variant: 'small',
        is_active: true
      });
    }
  }, [group]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim()) {
      alert(t('promo.enter_group_name'));
      return;
    }

    if (formData.font_size < 12 || formData.font_size > 48) {
      alert(t('promo.font_size_range_error'));
      return;
    }

    setIsLoading(true);
    try {
      if (group) {
        await PromoGroup.update(group.id, formData);
      } else {
        await PromoGroup.create(formData);
      }
      onSave();
    } catch (error) {
      console.error("Error saving promo group:", error);
              alert(t('promo.save_group_error'));
    } finally { // Use finally to ensure isLoading is always set to false
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-white"> {/* Changed max-w-2xl to max-w-3xl and added bg-white */}
        <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-slate-900">
            {isEditing ? t('promo.edit_promo_group') : t('promo.create_promo_group')}
            </DialogTitle>
            <DialogDescription className="text-slate-600">
            {isEditing ? t('promo.edit_group') : t('promo.description')}
            </DialogDescription>
        </DialogHeader>
        
        {/* The form element now wraps the content that needs to be submitted */}
        <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6 mt-6">
                <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <div>
                        <Label htmlFor="name">{t('promo.group_name')}</Label>
                        <Input 
                            id="name" 
                            value={formData.name} 
                            onChange={(e) => handleChange('name', e.target.value)} 
                            placeholder={t('promo.group_name_placeholder')} 
                            // `required` attribute was removed as per outline
                        />
                    </div>
                    <div>
                        <Label htmlFor="priority">{t('promo.display_priority')}</Label>
                        <Input 
                            id="priority" 
                            type="number" 
                            min="1" // Retained min attribute from original code
                            value={formData.priority} 
                            onChange={(e) => handleChange('priority', parseInt(e.target.value, 10))} 
                        />
                        <p className="text-xs text-slate-500 mt-1">{t('promo.priority_explanation')}</p>
                    </div>
                </div>

                <div className="md:col-span-2">
                    <h4 className="font-semibold text-slate-900 mb-3">{t('promo.header_style')}</h4>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-x-8 gap-y-4">
                        <div>
                            <Label htmlFor="font_size">{t('promo.font_size_px')}</Label>
                            <Input 
                                id="font_size" 
                                type="number" 
                                min="12" // Retained min attribute from original code
                                max="48" // Retained max attribute from original code
                                value={formData.font_size} 
                                onChange={(e) => handleChange('font_size', parseInt(e.target.value, 10))} 
                            />
                        </div>
                        <div>
                            <Label htmlFor="font_weight">{t('promo.font_weight')}</Label>
                            <Select 
                                value={formData.font_weight} 
                                onValueChange={(v) => handleChange('font_weight', v)}
                            >
                                <SelectTrigger><SelectValue/></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="normal">{t('promo.normal')}</SelectItem>
                                    <SelectItem value="semibold">{t('promo.semibold')}</SelectItem>
                                    <SelectItem value="bold">{t('promo.bold')}</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <ColorPickerInput
                                label={t('promo.text_color')}
                                value={formData.text_color}
                                onChange={(v) => handleChange('text_color', v)}
                            />
                        </div>
                    </div>
                </div>

                <div className="md:col-span-2">
                    <h4 className="font-semibold text-slate-900 mb-3">{t('promo.card_size')}</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
                        <div>
                            <Label htmlFor="card_variant">{t('promo.card_variant')}</Label>
                            <Select value={formData.card_variant} onValueChange={(v) => handleChange('card_variant', v)}>
                                <SelectTrigger><SelectValue/></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="small">{t('promo.variant_small')}</SelectItem>
                                    <SelectItem value="medium">{t('promo.variant_medium')}</SelectItem>
                                    <SelectItem value="large">{t('promo.variant_large')}</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-slate-500 mt-1">{t('promo.card_variant_explanation')}</p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-3">
                    <Switch 
                      id="is_active" 
                      checked={formData.is_active} 
                      onCheckedChange={(v) => handleChange('is_active', v)} 
                    />
                    <Label htmlFor="is_active" className="font-medium">{t('promo.active_group')}</Label>
                </div>
            </div>
            
            {/* Preview Section */}
            <div className="mt-8 p-6 bg-slate-50 rounded-lg">
                <h4 className="font-semibold text-slate-900 mb-4">{t('promo.preview_header')}</h4>
                <div className="p-4 bg-white rounded-md border border-slate-200">
                     <h3 
                        style={{
                          fontSize: `${formData.font_size}px`,
                          fontWeight: formData.font_weight,
                          color: formData.text_color,
                        }}
                      >
                        {formData.name || t('promo.group_name')}
                      </h3>
                      <p className="text-sm text-slate-500 mt-2">
                        {t('promo.card_variant_preview', { variant: formData.card_variant })}
                      </p>
                </div>
            </div>

            {/* DialogFooter remains outside the form, submit button uses onClick */}
            <DialogFooter className="mt-8">
                <Button type="button" variant="outline" onClick={onClose}>{t('promo.cancel')}</Button>
                <Button 
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="bg-amber-600 hover:bg-amber-700"
                >
                    {isLoading ? 'Сохранение...' : (isEditing ? 'Сохранить изменения' : 'Создать группу')}
                </Button>
            </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
